import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Shield, Star, TrendingUp, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProviderPayment } from '@/hooks/useProviderPayment';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { usePaymentPlatform } from '@/contexts/PaymentPlatformContext';
import { NativePaymentButton } from '@/components/NativePaymentButton';
import { PRODUCT_IDS, NATIVE_PRICES, WEB_PRICES } from '@/services/revenueCatService';
import { optimizeImage } from '@/utils/imageOptimization';

interface ProviderPaymentStepProps {
  photos: File[];
  credential: File | null;
  idDocumentFront: File | null;
  idDocumentBack: File | null;
  uploadedPhotoUrls?: string[];
  uploadedCredentialUrl?: string;
  uploadedIdFrontUrl?: string;
  uploadedIdBackUrl?: string;
}

export default function ProviderPaymentStep({ 
  photos, 
  credential, 
  idDocumentFront, 
  idDocumentBack,
  uploadedPhotoUrls = [],
  uploadedCredentialUrl = '',
  uploadedIdFrontUrl = '',
  uploadedIdBackUrl = ''
}: ProviderPaymentStepProps) {
  const { t } = useTranslation();
  const { initiateProviderSubscription, isProcessing, error } = useProviderPayment();
  const { isNative, paymentMethod } = usePaymentPlatform();

  // Dynamic pricing based on platform
  const providerPrice = isNative ? NATIVE_PRICES.provider.annual : WEB_PRICES.provider.annual;
  const setupFee = isNative ? 0 : WEB_PRICES.provider.setup;

  const benefits = [
    'services.providerSubscription.benefits.unlimitedListings',
    'services.providerSubscription.benefits.customerReviews',
    'services.providerSubscription.benefits.prioritySupport',
    'services.providerSubscription.benefits.analytics'
  ];

  const handlePayment = async () => {
    try {
      // Use already uploaded URLs or upload new files
      console.log('Preparing files for payment...');
      
      // Use existing photo URLs if available, otherwise upload new photos
      let photoUrls: string[] = [...uploadedPhotoUrls];
      if (photoUrls.length === 0 && photos.length > 0) {
        console.log('Uploading new photos...');
        for (const rawPhoto of photos) {
          // Optimize commercial photos before upload. ID documents and
          // credentials are intentionally NOT optimized (legal originals).
          const photo = rawPhoto.type.startsWith('image/')
            ? await optimizeImage(rawPhoto, { preset: 'feed' })
            : rawPhoto;
          const fileExt = photo.name.split('.').pop();
          const fileName = `temp/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

          const { error: uploadError } = await supabase.storage
            .from('provider-photos')
            .upload(fileName, photo);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('provider-photos')
            .getPublicUrl(fileName);

          photoUrls.push(publicUrl);
        }
      } else {
        console.log('Using existing photo URLs:', photoUrls.length);
      }
      
      // Use existing credential URL if available, otherwise upload new credential
      let credentialUrl = uploadedCredentialUrl;
      if (!credentialUrl && credential) {
        console.log('Uploading new credential...');
        const fileExt = credential.name.split('.').pop();
        const fileName = `temp/${Date.now()}_credential.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('provider-credentials')
          .upload(fileName, credential);
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('provider-credentials')
          .getPublicUrl(fileName);
        
        credentialUrl = publicUrl;
      } else {
        console.log('Using existing credential URL');
      }
      
      // Use already-uploaded ID document URLs if available, otherwise upload new files
      let idFrontUrl = uploadedIdFrontUrl;
      let idBackUrl = uploadedIdBackUrl;

      if (!idFrontUrl && idDocumentFront) {
        console.log('Uploading new ID front...');
        const fileExt = idDocumentFront.name.split('.').pop();
        const fileName = `temp/${Date.now()}_id_front.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('provider-id-documents')
          .upload(fileName, idDocumentFront);
        
        if (uploadError) throw uploadError;
        
        // Store path for private bucket (not public URL)
        idFrontUrl = fileName;
      } else if (idFrontUrl) {
        console.log('Using existing ID front URL');
      }
      
      if (!idBackUrl && idDocumentBack) {
        console.log('Uploading new ID back...');
        const fileExt = idDocumentBack.name.split('.').pop();
        const fileName = `temp/${Date.now()}_id_back.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('provider-id-documents')
          .upload(fileName, idDocumentBack);
        
        if (uploadError) throw uploadError;
        
        // Store path for private bucket (not public URL)
        idBackUrl = fileName;
      } else if (idBackUrl) {
        console.log('Using existing ID back URL');
      }
      
      // Store file URLs in sessionStorage
      sessionStorage.setItem('pendingProviderFileUrls', JSON.stringify({
        photoUrls,
        credentialUrl,
        idFrontUrl,
        idBackUrl
      }));
      
      console.log('Files uploaded successfully');
      await initiateProviderSubscription();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error(t('services.registerProvider.errorUploadingFiles'));
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card className="border-2 border-primary/20 shadow-lg">
        <CardHeader className="text-center pb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-primary/10 p-4 rounded-full">
              <Star className="w-12 h-12 text-primary" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold">
            {t('services.providerSubscription.title')}
          </CardTitle>
          <CardDescription className="text-lg mt-2">
            {t('services.providerSubscription.description')}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Precio - Dinámico según plataforma */}
          <div className="text-center py-6 bg-muted/50 rounded-lg">
            <div className="text-5xl font-bold text-primary">${providerPrice.toFixed(2)}</div>
            <div className="text-muted-foreground mt-2">
              USD / {t('common.year')}
            </div>
            {setupFee > 0 && (
              <div className="text-sm text-muted-foreground mt-1">
                {t('services.providerSubscription.setupFee')}: ${setupFee}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {t('services.providerSubscription.billedAnnually')}
            </div>
            {isNative && (
              <div className="text-xs text-primary mt-2 font-medium">
                {paymentMethod === 'apple' ? '🍎 Apple In-App Purchase' : '🤖 Google Play'}
              </div>
            )}
          </div>

          {/* Beneficios */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              {t('services.providerSubscription.whatsIncluded')}
            </h3>
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-start gap-3">
                <div className="bg-primary/10 p-1 rounded-full mt-0.5">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm">{t(benefit)}</span>
              </div>
            ))}
          </div>

          {/* Botón de Pago - Dinámico según plataforma */}
          <div className="pt-6">
            {isNative ? (
              <NativePaymentButton
                tier="provider"
                billingCycle="annual"
                onSuccess={() => {
                  toast.success(t('services.providerSubscription.paymentSuccess', { defaultValue: 'Payment successful!' }));
                  // The actual registration will be handled by the webhook
                }}
                onError={(err) => {
                  toast.error(err.message || t('services.providerSubscription.paymentError', { defaultValue: 'Payment failed' }));
                }}
              />
            ) : (
              <Button
                onClick={handlePayment}
                disabled={isProcessing}
                size="lg"
                className="w-full text-lg h-14 bg-[#0070BA] hover:bg-[#005EA6]"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    {t('services.providerSubscription.processing')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.067 8.478c.492.88.556 1.937.3 3.228-1.495 7.522-10.382 5.237-13.073 5.237l-1.138 4.528h-2.969l2.353-9.722c-1.301-.192-2.248-.4-2.248-.4l.462-1.828s2.85.498 4.337.498c1.488 0 1.825-.234 1.99-1.113l1.358-5.404c4.053 0 7.953.399 8.628 4.976zm-2.896.193c-.234-1.021-1.258-1.454-2.668-1.49h-.432l-1.328 5.83c2.324 0 4.599-1.346 4.428-4.34z" fill="currentColor"/>
                    </svg>
                    {t('services.providerSubscription.payWithPayPal')}
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-4 border-t">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span>{t('services.providerSubscription.securePayment')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span>{t('services.providerSubscription.poweredBy')}</span>
              <span className="font-semibold text-[#0070BA]">
                {isNative 
                  ? (paymentMethod === 'apple' ? 'Apple' : 'Google Play') 
                  : 'PayPal'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
